import React, { useEffect, useState } from 'react';
import {
    X, Loader2, Cctv, Network, Server, Monitor, Wifi, HardDrive, Printer, Shield, Router, Box, EthernetPort,
    Hash, Cpu, ShieldCheck, Activity, Globe, Clock, CheckCircle2, XCircle,
    Zap, Unplug, Fingerprint, Share2, Layers, Info, MapPin,
    Settings, ArrowDownLeft, ArrowUpRight
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { showSuccess, showError } from '../../../utils/toast';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface AssetDetailSidebarProps {
    open: boolean;
    assetId: number;
    assetName: string;
    assetType: string;
    assetIp: string;
    clientId: number;
    onClose: () => void;
}

type LucideIcon = React.FC<any>;

const getAssetIcon = (type: string): LucideIcon => {
    const icons: Record<string, LucideIcon> = {
        camera: Cctv, server: Server, workstation: Monitor, access_point: Wifi,
        nvr: HardDrive, printer: Printer, firewall: Shield, router: Router,
        switch: Router, storage: HardDrive, other: Box, local_asset: Server, isp: Wifi,
        hub: EthernetPort,
    };
    return icons[type] || Box;
};

// ─── Compact Card ───────────────────────────────────────────────────────────────

const DetailCard: React.FC<{ icon: LucideIcon; label: string; value: any; status?: boolean; color?: string }> = ({ icon: Icon, label, value, status, color = "text-blue-600" }) => (
    <div className="bg-white p-2.5 rounded-xl border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all group">
        <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-slate-50 rounded-lg group-hover:bg-blue-600 transition-colors shrink-0">
                <Icon className={`w-3.5 h-3.5 ${color} group-hover:text-white`} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight leading-none mb-1">{label}</p>
                {status !== undefined ? (
                    <div className="flex items-center gap-1">
                        {status ? <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" /> : <XCircle className="w-2.5 h-2.5 text-rose-500" />}
                        <span className={`text-[10px] font-bold leading-none ${status ? 'text-emerald-500' : 'text-rose-500'}`}>
                            {value}
                        </span>
                    </div>
                ) : (
                    <p className="text-[11px] font-bold text-slate-900 truncate leading-none" title={value?.toString()}>
                        {value || 'N/A'}
                    </p>
                )}
            </div>
        </div>
    </div>
);

// ─── Mini Table ─────────────────────────────────────────────────────────────────

const MiniTable: React.FC<{
    icon: LucideIcon;
    title: string;
    iconColor: string;
    columns: { key: string; label: string }[];
    data: any[];
    loading: boolean;
    itemsPerPage?: number;
    renderCell?: (key: string, item: any) => React.ReactNode;
}> = ({ icon: Icon, title, iconColor, columns, data, loading, itemsPerPage = 10, renderCell }) => {
    const [currentPage, setCurrentPage] = React.useState(1);
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    const pagedData = data.slice(start, start + itemsPerPage);

    React.useEffect(() => { setCurrentPage(1); }, [data]);

    return (
        <div className="overflow-hidden">
            <div className="px-1 py-1.5 flex items-center gap-1.5">
                <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">{title}</h3>
                {data.length > 0 && (
                    <span className="ml-auto text-[9px] text-slate-400 font-mono font-bold bg-slate-100 px-1.5 py-0.5 rounded-full">{data.length}</span>
                )}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                {columns.map((col) => (
                                    <th key={col.key} className="px-3 py-2 text-[9px] text-slate-500 uppercase tracking-wider font-bold">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-6 text-center">
                                        <Loader2 className="w-4 h-4 text-blue-500 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-4 text-center text-slate-400 text-[10px] italic">
                                        No data available
                                    </td>
                                </tr>
                            ) : (
                                pagedData.map((item, i) => (
                                    <tr key={item.id || start + i} className="hover:bg-slate-50/80 transition-colors group">
                                        {columns.map((col) => (
                                            <td key={col.key} className="px-3 py-2 text-[10px] font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                                                {renderCell ? renderCell(col.key, item) : (item[col.key] ?? '-')}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="bg-slate-50 px-3 py-1.5 border-t border-slate-100 flex justify-between items-center">
                        <span className="text-[10px] text-slate-500 font-bold">
                            {start + 1}–{Math.min(start + itemsPerPage, data.length)} of {data.length}
                        </span>
                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30 transition-colors bg-white border border-slate-200 rounded-lg shadow-sm"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <div className="bg-blue-50 border border-blue-200 text-blue-600 px-2 py-0.5 rounded-lg text-[10px] font-black min-w-[24px] text-center">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30 transition-colors bg-white border border-slate-200 rounded-lg shadow-sm"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── API-Paginated Mini Table ───────────────────────────────────────────────────

const ApiMiniTable: React.FC<{
    icon: LucideIcon;
    title: string;
    iconColor: string;
    columns: { key: string; label: string }[];
    endpoint: string;
    query: Record<string, any>;
    itemsPerPage?: number;
    renderCell?: (key: string, item: any) => React.ReactNode;
}> = ({ icon: Icon, title, iconColor, columns, endpoint, query, itemsPerPage = 10, renderCell }) => {
    const [data, setData] = useState<any[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const fetchData = React.useCallback(async (page: number) => {
        setLoading(true);
        try {
            const skip = (page - 1) * itemsPerPage;
            const res = await baseapi.get(endpoint, {
                params: { query: JSON.stringify(query), skip, limit: itemsPerPage }
            });
            if (res.data.status) {
                setData(res.data.data || []);
                setTotalItems(res.data.total || res.data.data?.length || 0);
            }
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, [endpoint, JSON.stringify(query), itemsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
        fetchData(1);
    }, [fetchData]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchData(page);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;

    return (
        <div className="overflow-hidden">
            <div className="px-1 py-1.5 flex items-center gap-1.5">
                <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">{title}</h3>
                {totalItems > 0 && (
                    <span className="ml-auto text-[9px] text-slate-400 font-mono font-bold bg-slate-100 px-1.5 py-0.5 rounded-full">{totalItems}</span>
                )}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                {columns.map((col) => (
                                    <th key={col.key} className="px-3 py-2 text-[9px] text-slate-500 uppercase tracking-wider font-bold">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-6 text-center">
                                        <Loader2 className="w-4 h-4 text-blue-500 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-4 text-center text-slate-400 text-[10px] italic">
                                        No data available
                                    </td>
                                </tr>
                            ) : (
                                data.map((item, i) => (
                                    <tr key={item.id || i} className="hover:bg-slate-50/80 transition-colors group">
                                        {columns.map((col) => (
                                            <td key={col.key} className="px-3 py-2 text-[10px] font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                                                {renderCell ? renderCell(col.key, item) : (item[col.key] ?? '-')}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="bg-slate-50 px-3 py-1.5 border-t border-slate-100 flex justify-between items-center">
                        <span className="text-[10px] text-slate-500 font-bold">
                            {start + 1}–{Math.min(start + itemsPerPage, totalItems)} of {totalItems}
                        </span>
                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30 transition-colors bg-white border border-slate-200 rounded-lg shadow-sm"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <div className="bg-blue-50 border border-blue-200 text-blue-600 px-2 py-0.5 rounded-lg text-[10px] font-black min-w-[24px] text-center">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30 transition-colors bg-white border border-slate-200 rounded-lg shadow-sm"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Camera Sidebar Content ─────────────────────────────────────────────────────

const CameraSidebarContent: React.FC<{
    assetId: number;
    client_id: any;
}> = ({ assetId, client_id }) => {
    const [overview, setOverview] = useState<any>(null);
    const [loadingOverview, setLoadingOverview] = useState(true);

    useEffect(() => {
        const query = JSON.stringify({ asset_id: assetId, client_id });

        baseapi.get('/asset_camera_details', { params: { query } })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) setOverview(res.data.data[0]);
            })
            .catch(() => { })
            .finally(() => setLoadingOverview(false));
    }, [assetId]);


    const formatDate = (dateString: string) => {
        if (!dateString || dateString === '-') return '-';
        try {
            const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : `${dateString}Z`;
            const date = new Date(normalized);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleString('en-IN', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: true,
            });
        } catch { return dateString; }
    };

    if (loadingOverview) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="relative">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                    <div className="absolute inset-0 bg-blue-500/10 blur-xl rounded-full"></div>
                </div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest animate-pulse">Fetching Camera Data</p>
            </div>
        );
    }

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Overview Cards */}
            {overview ? (
                <div className="grid grid-cols-2 gap-2">
                    <DetailCard icon={Hash} label="Serial" value={overview.serial_number} />
                    <DetailCard icon={Cpu} label="Firmware" value={overview.firmware} />
                    <DetailCard icon={ShieldCheck} label="Auth" value={overview.auth_success ? 'Success' : 'Failed'} status={overview.auth_success} />
                    <DetailCard icon={Activity} label="Status" value={overview.reachable ? 'Online' : 'Offline'} status={overview.reachable} />
                    <DetailCard icon={Wifi} label="DHCP" value={overview.dhcp === 'True' ? 'On' : 'Off'} status={overview.dhcp === 'True'} />
                    <DetailCard icon={Globe} label="ONVIF" value={overview.onvif_available ? 'Yes' : 'No'} status={overview.onvif_available} />
                    <DetailCard icon={Server} label="Method" value={overview.auth_method} />
                    <DetailCard icon={Clock} label="Updated" value={formatDate(overview.updated)} />
                </div>
            ) : (
                <div className="bg-slate-50 rounded-xl p-8 border border-slate-200 border-dashed text-center">
                    <Info className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs font-bold text-slate-400">No overview data available</p>
                </div>
            )}

            {/* Interfaces Table */}
            <ApiMiniTable
                icon={Monitor}
                title="Interfaces"
                iconColor="text-emerald-500"
                endpoint="/asset_camera_interfaces"
                query={{ asset_id: assetId, client_id }}
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'enabled', label: 'Status' },
                    { key: 'ipv4_address', label: 'IP' },
                    { key: 'mac_address', label: 'MAC' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'enabled') {
                        return (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-black uppercase ${item.enabled
                                ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                                : 'bg-rose-100 text-rose-600 border border-rose-200'
                                }`}>
                                {item.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                        );
                    }
                    if (key === 'ipv4_address') return <span className="text-blue-600 font-mono font-bold text-[10px]">{item[key] || '-'}</span>;
                    if (key === 'mac_address') return <span className="text-slate-500 font-mono text-[9px] font-medium">{item[key] || '-'}</span>;
                    return <span className="font-bold text-slate-700">{item[key] || '-'}</span>;
                }}
            />

            {/* Protocols Table */}
            <ApiMiniTable
                icon={ShieldCheck}
                title="Protocols"
                iconColor="text-blue-500"
                endpoint="/asset_camera_protocols"
                query={{ asset_id: assetId, client_id }}
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'enabled', label: 'Status' },
                    { key: 'ports', label: 'Ports' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'enabled') {
                        return (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-black uppercase ${item.enabled
                                ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                                : 'bg-rose-100 text-rose-600 border border-rose-200'
                                }`}>
                                {item.enabled ? 'On' : 'Off'}
                            </span>
                        );
                    }
                    if (key === 'ports') return <span className="text-blue-600 font-mono font-bold text-[10px]">{item[key] || '-'}</span>;
                    return <span className="font-bold text-slate-700">{item[key] || '-'}</span>;
                }}
            />

            {/* Access Control Table */}
            <ApiMiniTable
                icon={Info}
                title="Access Control"
                iconColor="text-purple-500"
                endpoint="/asset_camera_users"
                query={{ asset_id: assetId, client_id }}
                columns={[
                    { key: 'username', label: 'User' },
                    { key: 'user_level', label: 'Level' },
                    { key: 'auth_success', label: 'Auth' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'auth_success') {
                        if (item.auth_success === null) return <span className="text-[9px] text-slate-400 font-bold bg-slate-100 px-1 py-0.5 rounded">Pending</span>;
                        return item.auth_success
                            ? <span className="text-[9px] text-emerald-600 font-black flex items-center gap-1 uppercase tracking-tight"><CheckCircle2 className="w-3 h-3" /> SUCCESS</span>
                            : <span className="text-[9px] text-rose-600 font-black flex items-center gap-1 uppercase tracking-tight"><XCircle className="w-3 h-3" /> FAILED</span>;
                    }
                    if (key === 'user_level') {
                        return (
                            <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase bg-blue-50 text-blue-600 border border-blue-100 shadow-sm">
                                {item.user_level || 'Member'}
                            </span>
                        );
                    }
                    return <span className="font-bold text-slate-700">{item[key] || '-'}</span>;
                }}
            />
        </div>
    );
};

// ─── Switch Sidebar Content ─────────────────────────────────────────────────────
const formatBytes = (bytes: number | null) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const SwitchSidebarContent: React.FC<{ assetId: number }> = ({ assetId }) => {
    const [overview, setOverview] = useState<any>(null);
    const [poe, setPoe] = useState<any[]>([]);
    const [ports, setPorts] = useState<any[]>([]);
    const [loading, setLoading] = useState({
        overview: true, poe: true, ports: true
    });

    useEffect(() => {
        const query = JSON.stringify({ asset_id: assetId });

        baseapi.get('/switch_details', { params: { query } })
            .then(res => {
                if (res.data.status) {
                    setOverview(res.data.data?.[0] || res.data.data);
                }
            })
            .catch(() => { })
            .finally(() => setLoading(prev => ({ ...prev, overview: false })));

        baseapi.get('/poe_info', { params: { query, sort_by: 'created', sort_order: 'desc', limit: 1 } })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) {
                    const latest = res.data.data[0];
                    try {
                        const parsed = typeof latest.poe_data === 'string'
                            ? JSON.parse(latest.poe_data)
                            : (latest.poe_data || []);
                        setPoe(parsed);
                    } catch { setPoe([]); }
                }
            })
            .catch(() => { })
            .finally(() => setLoading(prev => ({ ...prev, poe: false })));

        baseapi.get('/ports', { params: { query } })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) {
                    const latestEntry = res.data.data[0];
                    try {
                        const parsedPorts = typeof latestEntry.ports === 'string'
                            ? JSON.parse(latestEntry.ports)
                            : (latestEntry.ports || []);
                        setPorts(parsedPorts);
                    } catch { setPorts([]); }
                }
            })
            .catch(() => { })
            .finally(() => setLoading(prev => ({ ...prev, ports: false })));
    }, [assetId]);

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Overview Cards */}
            {loading.overview ? (
                <div className="flex justify-center py-4">
                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                </div>
            ) : overview ? (
                <div className="grid grid-cols-2 gap-2">
                    <DetailCard icon={Network} label="Model" value={overview.model} color="text-indigo-600" />
                    <DetailCard icon={Hash} label="Serial" value={overview.serial_number} color="text-slate-600" />
                    <DetailCard icon={Cpu} label="IOS Version" value={overview.ios_version} color="text-orange-600" />
                    <DetailCard icon={Clock} label="Uptime" value={overview.uptime || 'N/A'} color="text-emerald-600" />
                    <DetailCard icon={Globe} label="IP Address" value={overview.ip_address} color="text-blue-600" />
                    <DetailCard icon={Layers} label="VLANs" value={overview.total_vlans} color="text-purple-600" />
                    <DetailCard icon={Activity} label="Ports Up" value={`${overview.total_ports_up || 0} / ${overview.total_interfaces || 0}`} color="text-emerald-600" />
                    <DetailCard icon={Fingerprint} label="MAC Entries" value={overview.total_mac_entries} color="text-rose-600" />
                    <DetailCard icon={MapPin} label="Location" value={overview.location} color="text-slate-500" />
                </div>
            ) : (
                <div className="bg-slate-50 rounded-xl p-8 border border-slate-200 border-dashed text-center">
                    <Info className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs font-bold text-slate-400">No switch data available</p>
                </div>
            )}

            {/* PoE Info */}
            <MiniTable
                icon={Zap}
                title="PoE Details"
                iconColor="text-orange-500"
                columns={[
                    { key: 'port', label: 'Port' },
                    { key: 'detection_status', label: 'Detection' },
                    { key: 'max_power_class', label: 'Limit' },
                    { key: 'power_consumption_watts', label: 'Usage' },
                ]}
                data={poe}
                loading={loading.poe}
                renderCell={(key, item) => {
                    if (key === 'detection_status') {
                        const val = item[key];
                        const isActive = val?.toLowerCase()?.includes('good') || val?.toLowerCase()?.includes('delivering');
                        return (
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[8px] font-black uppercase ${isActive
                                ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                                : 'bg-slate-100 text-slate-400 border border-slate-200'
                                }`}>
                                {val || '-'}
                            </span>
                        );
                    }
                    if (key === 'power_consumption_watts') return <span className="font-black text-slate-700">{item[key] ? `${item[key]}W` : '-'}</span>;
                    if (key === 'port') return <span className="font-bold text-blue-600">{item[key]}</span>;
                    return <span className="font-medium text-slate-600">{item[key] ?? '-'}</span>;
                }}
            />

            {/* Interface Details - API paginated */}
            <ApiMiniTable
                icon={Unplug}
                title="Interfaces"
                iconColor="text-blue-500"
                endpoint="/discovered_interfaces"
                query={{ switch_asset_id: assetId, interface_type: "ethernetCsmacd", }}
                columns={[
                    { key: 'port_name', label: 'Port' },
                    { key: 'port_status', label: 'Status' },
                    { key: 'port_speed', label: 'Speed' },
                    { key: 'vlan', label: 'VLAN' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'port_status') {
                        const isUp = item.port_status?.toLowerCase() === 'up' || item.port_status?.toLowerCase() === 'connected';
                        return (
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[8px] font-black uppercase ${isUp
                                ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                                : 'bg-rose-100 text-rose-600 border border-rose-200'
                                }`}>
                                {item.port_status || '-'}
                            </span>
                        );
                    }
                    if (key === 'port_name') return <span className="font-bold text-blue-600">{item.port_name || item.port_number || '-'}</span>;
                    if (key === 'vlan') return <span className="font-black text-slate-700">{item[key]}</span>;
                    return <span className="font-medium text-slate-600">{item[key] ?? '-'}</span>;
                }}
            />

            {/* MAC Address Entries - API paginated */}
            <ApiMiniTable
                icon={Fingerprint}
                title="MAC Entries"
                iconColor="text-orange-500"
                endpoint="/mac_table"
                query={{ asset_id: assetId }}
                columns={[
                    { key: 'mac_address', label: 'MAC' },
                    { key: 'bridge_port', label: 'Port' },
                    { key: 'vlan', label: 'VLAN' },
                    { key: 'vendor', label: 'Vendor' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'mac_address') return <span className="font-mono text-blue-600 font-bold text-[10px]">{item[key] || '-'}</span>;
                    if (key === 'vlan') return <span className="font-black text-slate-700">{item[key] ?? '-'}</span>;
                    return <span className="font-medium text-slate-600">{item[key] ?? '-'}</span>;
                }}
            />

            {/* Neighbors - API paginated */}
            <ApiMiniTable
                icon={Share2}
                title="Neighbors"
                iconColor="text-emerald-500"
                endpoint="/discovered_neighbors"
                query={{ asset_id: assetId, interface_type: "ethernetCsmacd" }}
                columns={[
                    { key: 'protocol', label: 'Protocol' },
                    { key: 'local_interface_name', label: 'Local' },
                    { key: 'neighbor_device_id', label: 'Device' },
                    { key: 'neighbor_platform', label: 'Platform' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'protocol') {
                        return (
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[8px] font-black uppercase ${item.protocol === 'lldp'
                                ? 'bg-blue-50 text-blue-600 border border-blue-100'
                                : 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                                }`}>
                                {item.protocol || '-'}
                            </span>
                        );
                    }
                    if (key === 'local_interface_name') return <span className="font-bold text-blue-600">{item[key]}</span>;
                    return <span className="font-medium text-slate-600">{item[key] || '-'}</span>;
                }}
            />

            {/* VLAN Configuration - API paginated */}
            <ApiMiniTable
                icon={Layers}
                title="VLANs"
                iconColor="text-purple-500"
                endpoint="/vlans"
                query={{ asset_id: assetId }}
                columns={[
                    { key: 'vlan_id', label: 'ID' },
                    { key: 'name', label: 'Name' },
                    { key: 'status', label: 'Status' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'status') {
                        const isActive = item.status?.toLowerCase() === 'active';
                        return (
                            <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${isActive
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-gray-700/50 text-gray-500 border border-gray-600/20'
                                }`}>
                                {item.status || '-'}
                            </span>
                        );
                    }
                    if (key === 'vlan_id') return <span className="font-mono text-orange-400 text-[9px]">{item[key]}</span>;
                    return item[key] || '-';
                }}
            />

            {/* Switch Ports */}
            <MiniTable
                icon={Activity}
                title="Switch Ports"
                iconColor="text-cyan-500"
                columns={[
                    { key: 'port', label: 'Port' },
                    { key: 'state', label: 'State' },
                    { key: 'service', label: 'Service' },
                ]}
                data={ports}
                loading={loading.ports}
                renderCell={(key, item) => {
                    if (key === 'state') {
                        const isOpen = item.state?.toLowerCase() === 'open';
                        return (
                            <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${isOpen
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-red-500/10 text-red-500 border border-red-500/20'
                                }`}>
                                {item.state || '-'}
                            </span>
                        );
                    }
                    return item[key] ?? '-';
                }}
            />
        </div>
    );
};

// ─── Access Point Sidebar Content ───────────────────────────────────────────────

const AccessPointSidebarContent: React.FC<{ assetId: number }> = ({ assetId }) => {
    const [overview, setOverview] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const safeParseJSON = (jsonString: string | null) => {
        if (!jsonString) return {};
        try {
            return JSON.parse(jsonString);
        } catch (e) {
            return {};
        }
    };

    useEffect(() => {
        const query = JSON.stringify({ asset_id: assetId });
        setLoading(true);
        baseapi.get('/ap_asset_detail', { params: { query } })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) {
                    setOverview(res.data.data[0]);
                } else if (res.data.id) {
                    setOverview(res.data);
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [assetId]);

    if (loading) {
        return (
            <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
            </div>
        );
    }

    if (!overview) {
        return (
            <div className="bg-slate-50 rounded-xl p-8 border border-slate-200 border-dashed text-center">
                <Info className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-xs font-bold text-slate-400">No AP data available</p>
            </div>
        );
    }

    const sysInfo = safeParseJSON(overview.system_info);
    const wireless = safeParseJSON(overview.wireless);
    const health = safeParseJSON(overview.health);
    const stations = wireless?.connected_stations || [];

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Overview Grid */}
            <div className="grid grid-cols-2 gap-2">
                <DetailCard icon={Network} label="Vendor" value={overview.vendor} color="text-indigo-600" />
                <DetailCard icon={Cpu} label="Firmware" value={wireless.software_version || overview.manufacturer || "Unknown"} color="text-orange-600" />
                <DetailCard icon={Clock} label="Uptime" value={sysInfo.uptime || "N/A"} color="text-emerald-600" />
                <DetailCard icon={Activity} label="Health" value={health.status || "Healthy"} status={health.status?.toLowerCase() !== 'critical'} />
            </div>

            {/* Wireless Configuration */}
            <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                    <Wifi className="w-3.5 h-3.5 text-purple-600" />
                    <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Wireless Setup</h4>
                </div>
                <div className="space-y-3">
                    <div className="flex justify-between items-center group">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Network SSID</span>
                        <span className="text-[11px] font-black text-purple-600 group-hover:bg-purple-50 px-1.5 py-0.5 rounded transition-all">{wireless.ssid || "N/A"}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-1">
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter mb-0.5">Frequency</p>
                            <p className="text-[11px] text-slate-700 font-mono font-bold italic">{wireless.radio?.frequency_mhz ? `${wireless.radio.frequency_mhz} MHz` : "N/A"}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter mb-0.5">TX Power</p>
                            <p className="text-[11px] text-slate-700 font-mono font-bold italic">{wireless.radio?.tx_power_dbm ? `${wireless.radio.tx_power_dbm} dBm` : "N/A"}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Radio Metrics */}
            <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                    <Activity className="w-3.5 h-3.5 text-emerald-600" />
                    <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Radio Metrics</h4>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-2 bg-slate-50 rounded-lg border border-slate-100">
                        <p className="text-[8px] text-slate-400 font-black uppercase mb-1">Quality</p>
                        <p className="text-[11px] text-emerald-600 font-black">{wireless.radio?.signal_quality || "N/A"}</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg border border-slate-100">
                        <p className="text-[8px] text-slate-400 font-black uppercase mb-1">RSSI</p>
                        <p className="text-[11px] text-slate-900 font-mono font-bold">{wireless.radio?.rssi_dbm ? `${wireless.radio.rssi_dbm} dBm` : "N/A"}</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg border border-slate-100">
                        <p className="text-[8px] text-slate-400 font-black uppercase mb-1">SNR</p>
                        <p className="text-[11px] text-slate-900 font-mono font-bold">{wireless.radio?.snr_db ? `${wireless.radio.snr_db} dB` : "N/A"}</p>
                    </div>
                </div>
            </div>

            {/* Interfaces Table */}
            <ApiMiniTable
                icon={Network}
                title="Interfaces"
                iconColor="text-blue-500"
                endpoint="/ap_interfaces"
                query={{ asset_id: assetId }}
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'oper_status', label: 'Status' },
                    { key: 'mac_address', label: 'MAC' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'oper_status') {
                        const isUp = item.oper_status?.toLowerCase() === 'up';
                        return (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm ${isUp
                                ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                                : 'bg-rose-100 text-rose-600 border border-rose-200'
                                }`}>
                                {item.oper_status || 'Down'}
                            </span>
                        );
                    }
                    if (key === 'mac_address') return <span className="text-[10px] font-mono text-slate-400 font-medium">{item[key]}</span>;
                    if (key === 'name') return <span className="text-[11px] font-bold text-slate-700">{item[key]}</span>;
                    return <span className="text-slate-600">{item[key] || '-'}</span>;
                }}
            />

            {/* Connected Stations Table */}
            <MiniTable
                icon={Monitor}
                title="Connected Stations"
                iconColor="text-indigo-500"
                columns={[
                    { key: 'mac_address', label: 'MAC' },
                    { key: 'signal_dbm', label: 'Signal' },
                    { key: 'uptime', label: 'Uptime' },
                ]}
                data={stations}
                loading={false}
                renderCell={(key, item) => {
                    if (key === 'mac_address') return <span className="font-mono text-indigo-600 text-[10px] font-bold italic">{item[key]}</span>;
                    if (key === 'signal_dbm') return <span className="text-[11px] text-slate-900 font-mono font-black">{item[key]}</span>;
                    return <span className="text-slate-600 font-medium">{item[key] || '-'}</span>;
                }}
            />
        </div>
    );
};

// ─── Port Sidebar Content ───────────────────────────────────────────────────────

const PortSidebarContent: React.FC<{ interfaceId: number }> = ({ interfaceId }) => {
    const [selectedInterface, setSelectedInterface] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        baseapi.get(`/discovered_interfaces`, {
            params: {
                query: JSON.stringify({ id: interfaceId })
            }
        })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) {
                    setSelectedInterface(res.data.data[0]);
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [interfaceId]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="relative">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                    <div className="absolute inset-0 bg-blue-500/10 blur-xl rounded-full"></div>
                </div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest animate-pulse">Analyzing Interface</p>
            </div>
        );
    }

    if (!selectedInterface) {
        return (
            <div className="bg-slate-50 rounded-xl p-8 border border-slate-200 border-dashed text-center">
                <Info className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-xs font-bold text-slate-400">Interface details not found</p>
            </div>
        );
    }

    // Helper for rendering rows with a premium look
    const renderDetailRow = (label: string, value: any, suffix: string = "", color: string = "text-slate-600") => (
        <div className="flex justify-between items-center py-1.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50 px-1 -mx-1 rounded transition-colors group">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight group-hover:text-slate-500 transition-colors">{label}</span>
            <span className={`text-[11px] font-black ${color} text-right max-w-[65%] truncate font-mono`} title={String(value)}>
                {value !== null && value !== undefined && value !== "" ? `${value}${suffix}` : "-"}
            </span>
        </div>
    );

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Header Area */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full blur-3xl -mr-16 -mt-16 opacity-50 group-hover:bg-blue-100 transition-colors"></div>
                <div className="relative z-10">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-base font-black text-slate-900 tracking-tight">
                            {selectedInterface.port_name || selectedInterface.port_number || 'Unknown Interface'}
                        </h2>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black uppercase shadow-sm border ${selectedInterface.port_status === 'up' && selectedInterface.connected_asset_id != null
                                ? 'bg-amber-50 text-amber-600 border-amber-200'
                                : selectedInterface.port_status === 'up'
                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                    : 'bg-rose-50 text-rose-600 border-rose-200'
                            }`}>
                            {selectedInterface.port_status === 'up' && selectedInterface.connected_asset_id != null
                                ? 'Used'
                                : selectedInterface.port_status}
                        </span>
                    </div>

                    <div className="flex items-center justify-between">
                        <span className="text-[11px] text-slate-500 font-mono font-bold tracking-tighter bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                            {selectedInterface.mac_address || 'No MAC Address'}
                        </span>
                        <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-tight text-slate-400">
                            <span>Last Change:</span>
                            <span className="text-slate-600">{selectedInterface.last_change || 'Never'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Connected Asset Section */}
            {selectedInterface.connected_asset_name && (
                <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all group">
                    <div className="flex items-center gap-2 mb-3">
                        <Share2 className="w-3.5 h-3.5 text-blue-600" />
                        <h3 className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Connected Asset</h3>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="p-2.5 bg-blue-600 rounded-xl shadow-lg shadow-blue-200 group-hover:scale-105 transition-transform">
                            <Monitor className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-black text-slate-900 group-hover:text-blue-700 transition-colors line-clamp-1">{selectedInterface.connected_asset_name}</p>
                            <p className="text-[11px] font-bold text-slate-500 font-mono italic">{selectedInterface.connected_asset_ip || 'No IP'}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* 1. Configuration */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                    <Settings className="w-4 h-4 text-slate-700" />
                    <h4 className="text-slate-900 text-[11px] font-black uppercase tracking-wider">Configuration</h4>
                </div>
                <div className="space-y-0.5">
                    {renderDetailRow("Admin Status", selectedInterface.admin_status)}
                    {renderDetailRow("Port Speed", selectedInterface.port_speed)}
                    {renderDetailRow("Raw Speed (bps)", selectedInterface.raw_speed_bps)}
                    {renderDetailRow("MTU (bytes)", selectedInterface.mtu)}
                    {renderDetailRow("VLAN", selectedInterface.vlan)}
                    {renderDetailRow("Native VLAN", selectedInterface.native_vlan)}
                </div>
            </div>

            {/* 2. Traffic & Packets */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
                    <Activity className="w-4 h-4 text-blue-600" />
                    <h4 className="text-slate-900 text-[11px] font-black uppercase tracking-wider">Traffic Analysis</h4>
                </div>

                <div className="space-y-6">
                    {/* INBOUND */}
                    <div>
                        <h5 className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                            <ArrowDownLeft className="w-3 h-3" /> Inbound (RX)
                        </h5>
                        <div className="space-y-0.5 pl-4 border-l-2 border-emerald-50">
                            {renderDetailRow("RX Traffic", formatBytes(selectedInterface.in_octets))}
                            {renderDetailRow("RX Unicast", selectedInterface.in_unicast_packets?.toLocaleString())}
                            {renderDetailRow("RX Non-Unicast", selectedInterface.in_non_unicast_packets?.toLocaleString())}
                            {renderDetailRow("RX Errors", selectedInterface.in_errors?.toLocaleString(), "", "text-rose-600")}
                            {renderDetailRow("RX Discards", selectedInterface.in_discards?.toLocaleString(), "", "text-rose-600")}
                            {renderDetailRow("Loss Rx", selectedInterface.packet_loss_in_loss_percentage, "%", "text-rose-600")}
                        </div>
                    </div>

                    {/* OUTBOUND */}
                    <div>
                        <h5 className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                            <ArrowUpRight className="w-3 h-3" /> Outbound (TX)
                        </h5>
                        <div className="space-y-0.5 pl-4 border-l-2 border-blue-50">
                            {renderDetailRow("TX Traffic", formatBytes(selectedInterface.out_octets))}
                            {renderDetailRow("TX Unicast", selectedInterface.out_unicast_packets?.toLocaleString())}
                            {renderDetailRow("TX Non-Unicast", selectedInterface.out_non_unicast_packets?.toLocaleString())}
                            {renderDetailRow("TX Errors", selectedInterface.out_errors?.toLocaleString(), "", "text-rose-600")}
                            {renderDetailRow("TX Discards", selectedInterface.out_discards?.toLocaleString(), "", "text-rose-600")}
                            {renderDetailRow("Loss Tx", selectedInterface.packet_loss_out_loss_percentage, "%", "text-rose-600")}
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. System Metadata */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                    <Info className="w-4 h-4 text-slate-700" />
                    <h4 className="text-slate-900 text-[11px] font-black uppercase tracking-wider">System Metadata</h4>
                </div>
                <div className="space-y-0.5">
                    {renderDetailRow("Index", selectedInterface.port_index)}
                    {renderDetailRow("Interface Type", selectedInterface.interface_type)}
                    {renderDetailRow("Conn Type", selectedInterface.connection_type)}

                    <div className="mt-3 pt-3 border-t border-slate-50">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-tight block mb-2">Description</span>
                        <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 text-[11px] text-slate-600 font-medium min-h-[50px] italic">
                            {selectedInterface.port_description && selectedInterface.port_description !== '""' ? selectedInterface.port_description : "No description provided"}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Main Sidebar Component ─────────────────────────────────────────────────────

const AssetDetailSidebar: React.FC<AssetDetailSidebarProps> = ({
    open,
    assetId,
    assetName,
    assetType,
    assetIp,
    clientId,
    onClose,
}) => {

    const client_id = clientId;

    const handleMarkPrimary = async () => {
        try {
            const response = await baseapi.post('topology/mark-primary', {
                asset_id: assetId,
                client_id: client_id,
                asset_type: assetType
            });
            if (response.data.status) {
                showSuccess(response.data.message || "Marked as primary successfully");
            } else {
                showError(response.data.message || "Failed to mark as primary");
            }
        } catch (error: any) {
            showError(error.message);
        }
    };
    const Icon = getAssetIcon(assetType);

    const isSwitchType = assetType === 'switch' || assetType === 'firewall';
    const isCameraType = assetType === 'camera';
    const isAccessPointType = assetType === 'access_point';
    const isPort = assetType === 'interface';

    const typeLabel = assetType.charAt(0).toUpperCase() + assetType.slice(1).replace(/_/g, ' ');

    return (
        <div
            className={`h-full bg-white transition-all duration-300 ease-in-out flex flex-col shadow-[-10px_0_30px_rgba(0,0,0,0.04)] border-slate-200 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
            style={{
                width: open ? '380px' : '0px',
                minWidth: open ? '380px' : '0px',
                borderLeftWidth: open ? '1px' : '0px',
                overflow: 'hidden'
            }}
        >
            <div className="h-full flex flex-col min-w-[380px] bg-slate-50/10">
                {/* Header */}
                <div className="px-5 py-4 border-b border-slate-100 flex-shrink-0 bg-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className={`p-2 rounded-xl shadow-lg transform transition-transform group-hover:scale-110 ${isCameraType
                                    ? 'bg-orange-600 shadow-orange-100'
                                    : isSwitchType
                                        ? 'bg-blue-600 shadow-blue-100'
                                        : isAccessPointType
                                            ? 'bg-purple-600 shadow-purple-100'
                                            : 'bg-slate-600 shadow-slate-100'
                                }`}>
                                <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div className="min-w-0">
                                <h2
                                    className="text-base font-black text-slate-900 leading-tight max-w-[180px] truncate"
                                    title={assetName}
                                >
                                    {assetName}
                                </h2>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter border ${isCameraType
                                            ? 'bg-orange-50 text-orange-600 border-orange-100'
                                            : isSwitchType
                                                ? 'bg-blue-50 text-blue-600 border-blue-100'
                                                : isAccessPointType
                                                    ? 'bg-purple-50 text-purple-600 border-purple-100'
                                                    : 'bg-slate-50 text-slate-600 border-slate-100'
                                        }`}>
                                        {typeLabel}
                                    </span>
                                    <span className="text-[11px] text-slate-400 font-mono font-bold">{assetIp}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {isSwitchType && (
                                <button
                                    onClick={handleMarkPrimary}
                                    className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase bg-white text-orange-600 border border-orange-200 shadow-sm hover:bg-orange-600 hover:text-white hover:border-orange-600 transition-all active:scale-95"
                                >
                                    Is Primary
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl bg-slate-50 border border-slate-100 text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all active:scale-90"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scroll p-4 space-y-4">
                    {isCameraType && (
                        <CameraSidebarContent
                            client_id={client_id}
                            assetId={assetId}
                        />
                    )}
                    {isSwitchType && <SwitchSidebarContent assetId={assetId} />}
                    {isAccessPointType && <AccessPointSidebarContent assetId={assetId} />}
                    {isPort && <PortSidebarContent interfaceId={assetId} />}
                    {!isCameraType && !isSwitchType && !isAccessPointType && !isPort && (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <div className="p-6 bg-slate-50 rounded-full mb-4 border border-slate-100 shadow-inner">
                                <Icon className="w-12 h-12 text-slate-300" />
                            </div>
                            <h3 className="text-sm font-black text-slate-900 mb-2">Asset Intelligence</h3>
                            <p className="text-[11px] text-slate-500 max-w-[250px] leading-relaxed mb-6 font-medium">
                                Advanced diagnostic data and real-time metrics are available for Cameras and Switches.
                            </p>
                            <div className="grid grid-cols-2 gap-3 w-full max-w-[320px]">
                                <DetailCard icon={Box} label="Type" value={typeLabel} color="text-slate-600" />
                                <DetailCard icon={Globe} label="IP Address" value={assetIp} color="text-blue-600" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AssetDetailSidebar;
